<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Material..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.back()">
                    <i class="fa fa-arrow-left"></i> Materials
                </a>
                <span class="sep">/</span>
                <span class="current">{{ material.name || 'Material' }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-accent-light);color:var(--pp-accent);">
                    <img v-if="material.photo" :src="getFileUrl(material.photo)"
                        style="width:100%;height:100%;object-fit:cover;border-radius:12px;" alt="Material" />
                    <i v-else class="fa fa-box"></i>
                </div>
                <div class="profile-header-body">
                    <h3>{{ material.name || 'Unnamed Material' }} - <span style="text-transform: capitalize;">({{ material.type || ' ' }})</span></h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ material.external_id || 'N/A' }}</span>
                        <span v-if="material.code"><i class="fa fa-barcode"></i>{{ material.code }}</span>
                        <span v-if="material.manufacturer"><i class="fa fa-industry"></i>{{ material.manufacturer }}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge" :class="statusBadgeClass">
                        <span class="dot"></span> {{ statusLabel }}
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click="editMaterial(material)">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-weight-hanging"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Quantity</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ material.quantity || 0 }} {{ material.unit || '' }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-shopping-cart"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Total Ordered</div>
                        <div class="profile-stat-value">{{ material.quantity || '' }}{{ material.unit || '' }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-truck"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Vendors</div>
                        <div class="profile-stat-value">{{ vendors.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Created</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(material.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: Material details -->
                <div class="col-lg-8 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> Material Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">
                                <div class="profile-field" v-if="material.code">
                                    <span class="profile-label">Code</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ material.code }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.manufacturer">
                                    <span class="profile-label">Manufacturer</span>
                                    <span class="profile-value">{{ material.manufacturer }}</span>
                                </div>
                                <div class="profile-field" v-if="material.quantity">
                                    <span class="profile-label">Quantity</span>
                                    <span class="profile-value">{{ material.quantity }} {{ material.unit || '' }}</span>
                                </div>
                                <div class="profile-field" v-if="categoryName">
                                    <span class="profile-label">Category</span>
                                    <span class="profile-value">
                                        <span class="profile-badge c-accent">{{ categoryName }}</span>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.serial_number">
                                    <span class="profile-label">Serial Number</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ material.serial_number }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(material.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Description: full-width field -->
                            <div class="profile-field mt-3" v-if="material.description"
                                style="grid-column:1/-1;">
                                <span class="profile-label">Description</span>
                                <span class="profile-value" style="line-height:1.6;">
                                    {{ material.description }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Work Order Schedules -->
                    <div class="profile-card" v-if="material.get_wo_materials && material.get_wo_materials.length > 0">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-calendar" style="color:var(--pp-accent);"></i> Work Order Schedules
                            </div>
                            <span class="profile-count-pill">{{ material.get_wo_materials.length }}</span>
                        </div>
                        <div class="profile-card-body p-0" style="overflow:auto">
                            <div class="table-responsive">
                                <table class="profile-table">
                                    <thead>
                                        <tr>
                                            <th>Quantity</th>
                                            <th>Schedule Date</th>
                                            <th>Work Order</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(woMaterial, i) in material.get_wo_materials" :key="woMaterial.id || i">
                                            <td>
                                                <span class="profile-badge c-info" style="font-size:12px;">
                                                    {{ woMaterial.quantity || 0 }} {{ material.unit || '' }}
                                                </span>
                                            </td>
                                            <td class="profile-value" style="font-size:13px;">
                                                {{ woMaterial.get_schedule?.schedule_date || '—' }}
                                            </td>
                                            <td>
                                                <span class="profile-value" style="font-size:13px;">
                                                    {{ woMaterial.get_schedule?.get_parent?.get_work_order?.name || '—' }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Purchase Order History -->
                    <div class="profile-card" v-if="poItems.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-amber-text);">
                                <i class="fa fa-shopping-cart" style="color:var(--pp-amber-text);"></i> Purchase Order History
                            </div>
                            <span class="profile-count-pill">{{ poItems.length }}</span>
                        </div>
                        <div class="profile-card-body p-0">
                            <div class="table-responsive">
                                <table class="profile-table">
                                    <thead>
                                        <tr>
                                            <th>PO Number</th>
                                            <th>Date</th>
                                            <th>Vendor</th>
                                            <th>Qty</th>
                                            <th>Current Qty</th>
                                            <!-- <th>Unit Price</th>
                                            <th>Total</th> -->
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, i) in poItems" :key="item.id || i">
                                            <td>
                                                <router-link
                                                    v-if="item.purchase_order?.external_id || item.purchase_order?.id"
                                                    :to="getPurchaseOrderRoute(item.purchase_order)"
                                                    class="profile-value text-decoration-none"
                                                    style="font-family:monospace;font-size:13px;color:var(--pp-accent);"
                                                >
                                                    {{ item.purchase_order?.external_id || '—' }}
                                                </router-link>
                                                <span v-else class="profile-value" style="font-family:monospace;font-size:13px;">
                                                    —
                                                </span>
                                            </td>
                                            <td class="profile-value text-muted" style="font-size:13px;">
                                                {{ formatDate(item.purchase_order?.created_at) || '—' }}
                                            </td>
                                            <td>
                                                <span class="profile-value" style="font-size:13px;">
                                                    {{
                                                        item.purchase_order?.vendor?.get_profile?.company_name
                                                        || item.purchase_order?.vendor?.first_name
                                                        || '—'
                                                    }}
                                                </span>
                                            </td>
                                            <td>
                                                <span class="profile-badge c-info" style="font-size:12px;">
                                                    {{ item.quantity }} {{ item.unit }}
                                                </span>
                                            </td>
                                              <td>
                                                <span class="profile-badge c-info" style="font-size:12px;">
                                                    {{ item.current_quantity }} {{ item.unit }}
                                                </span>
                                            </td>
                                            <!-- <td class="profile-value" style="font-size:13px;">
                                                ${{ formatPrice(item.unit_price) }}
                                            </td>
                                            <td class="profile-value" style="font-size:13px;font-weight:600;">
                                                ${{ formatPrice(item.total_price) }}
                                            </td> -->
                                            <td>
                                                <span class="profile-badge" :class="poStatusBadgeClass(item.purchase_order?.status)">
                                                    {{ labelStatus(item.purchase_order?.status) }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Attachment -->
                    <div class="profile-card" v-if="material.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-paperclip"></i> Document Attachment
                            </div>
                            <span class="profile-count-pill">1 file</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="row g-2">
                                <div class="col-6 col-sm-4 col-md-3">
                                    <div class="profile-attachment">
                                        <div class="profile-attachment-thumb">
                                            <i class="fa fa-file-pdf-o"
                                                style="font-size:24px;color:var(--pp-text-muted);"></i>
                                        </div>
                                        <div class="profile-attachment-meta">
                                            <div class="profile-attachment-name">Material File</div>
                                            <a :href="getFileUrl(material.file)" target="_blank"
                                                class="profile-attachment-size"
                                                style="color:var(--pp-accent);text-decoration:none;font-size:11px;">
                                                <i class="fa fa-download"></i> Download
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Connections sidebar -->
                <div class="col-lg-4 d-flex flex-column gap-3">

                    <!-- Vendors -->
                    <div class="profile-card" v-if="vendors.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-amber-text);">
                                <i class="fa fa-truck" style="color:var(--pp-amber-text);"></i> Vendors
                            </div>
                            <span class="profile-count-pill">{{ vendors.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <router-link v-for="(v, i) in vendors" :key="v.id || i"
                                    :to="getVendorRoute(v)"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;text-decoration:none;color:inherit;">
                                    <div class="profile-avatar c-warning" style="width:28px;height:28px;font-size:11px;">
                                        {{ vendorName(v).charAt(0).toUpperCase() }}
                                    </div>
                                    <span class="profile-value" style="font-size:13px;">
                                        {{ vendorName(v) }}
                                    </span>
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <!-- Linked Assets -->
                    <div class="profile-card" v-if="assets.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-cubes" style="color:var(--pp-accent);"></i> Linked Assets
                            </div>
                            <span class="profile-count-pill">{{ assets.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <router-link v-for="(a, i) in assets" :key="a.id || i"
                                    :to="getAssetRoute(a)"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;text-decoration:none;color:inherit;">
                                    <div class="profile-avatar c-accent" style="width:28px;height:28px;font-size:11px;">
                                        <i class="fa fa-microchip" style="font-size:12px;"></i>
                                    </div>
                                    <span class="profile-value" style="font-size:13px;">
                                        {{ a.name }}
                                    </span>
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <!-- Empty connections state -->
                    <div class="profile-card"
                        v-if="!vendors.length && !assets.length && !material.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-plug"></i> Connections
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-empty">
                                <i class="fa fa-plug"></i>
                                <p>No connections yet</p>
                                <span style="font-size:12px;color:var(--pp-text-muted);">
                                    This material isn't linked to any vendors or assets
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>
     <FormModal v-if="showEditModal" ref="editModal" title="Edit Material" id="edit_material_form_modal">
        <EditMaterial :materialId="editingMaterialId" @created="onCreated" @close="closeEditModal" />
      </FormModal>

</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditMaterial from './EditMaterial.vue';

export default {
    name: 'MaterialProfile',
    components: { LoadingBar,
        FormModal,
        EditMaterial
     },

    data() {
        return {
            material: {},
            loading: false,
            external_id: this.$route.params.external_id,
                  editingMaterialId: null,
      showEditModal: false,
        }
    },

    computed: {
        categoryName() {
            return this.material.get_category?.name || ''
        },

        vendors() {
            return (this.material.get_vendors || []).filter(Boolean)
        },

        assets() {
            return (this.material.get_assets || [])
                .map(a => a.get_asset || a)
                .filter(Boolean)
        },

        poItems() {
            return this.material.get_po_items || []
        },

        // totalOrdered() {
        //     return this.poItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
        // },

        openPoCount() {
            return this.poItems.filter(
                item => item.purchase_order?.status === 'open' || item.purchase_order?.status === 'pending'
            ).length
        },
        statusLabel() {
            return this.material.status || 'Active'
        },

        statusBadgeClass() {
            const status = this.material.status?.toLowerCase() || 'active'
            const map = {
                'active': 'c-success',
                'inactive': 'c-danger',
                'pending': 'c-warning',
                'draft': 'c-info'
            }
            return map[status] || 'c-success'
        },

    },

    mounted() {
        this.fetchProfile()
    },

    methods: {
        formatDate(date) {
            if (!date) return '—'
            try {
                return new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            } catch {
                return date
            }
        },

        vendorName(v) {
            const first = v.first_name || ''
            const last = v.last_name || ''
            return (first + ' ' + last).trim() || v.get_profile?.company_name || 'Vendor'
        },

        formatPrice(val) {
            return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        labelStatus(status) {
            const map = { open: 'Open', pending: 'Pending', received: 'Received', closed: 'Closed' }
            return map[status] || status || '—'
        },
        poStatusBadgeClass(status) {
            const map = {
                'open': 'c-info',
                'pending': 'c-warning',
                'received': 'c-success',
                'closed': 'c-secondary'
            }
            return map[status] || 'c-secondary'
        },
        async fetchProfile() {
            this.loading = true
            const result = await form_api_submit({
                url: `materials/profile/${this.external_id}`,
                method: 'GET',
                data: {},
                show_success_toast: false,
                show_error_toast: true,
                remove_pre_toasts: false,
            })
            if (result.success) {
                this.material = result.data
            }
            this.loading = false
        },

        getFileUrl(filePath) {
            return resolveAssetFileUrl(filePath)
        },
        getVendorRoute(vendor) {
            const externalId = vendor?.external_id || vendor?.id
            return externalId ? { name: 'VendorProfile', params: { external_id: externalId } } : null
        },
        getAssetRoute(asset) {
            const externalId = asset?.external_id || asset?.id
            return externalId ? { name: 'AssetProfile', params: { external_id: externalId } } : null
        },
        getPurchaseOrderRoute(purchaseOrder) {
            const externalId = purchaseOrder?.external_id || purchaseOrder?.id
            return externalId ? { name: 'OrderProfile', params: { external_id: externalId } } : null
        },
         closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },

    async onCreated() {
      await this.fetchProfile()
    },
         editMaterial(material) {
            console.log(material);
      this.editingMaterialId = material.id
      this.showEditModal = true
      this.$nextTick(() => {
        this.$refs.editModal.showModal()
      })
    },
    closeEditModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].closeModal()
      }

      this.editingMaterialId = null
      this.showEditModal = false
    },
    },

}
</script>

<style scoped>
/* ====== GLOBAL VARIABLES ====== */
:root {
    --pp-bg: #f6f8fc;
    --pp-card-bg: #ffffff;
    --pp-border: #e5e9f0;
    --pp-text: #1a2634;
    --pp-text-muted: #6b7a8f;
    --pp-accent: #2a6df4;
    --pp-accent-light: #eef3fe;
    --pp-amber-text: #b45309;
    --pp-amber-light: #fffbeb;
}

.profile-page {
    background: var(--pp-bg);
    min-height: 100vh;
    padding: 20px 0 40px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: var(--pp-text);
}

.container-xl {
    max-width: 1280px;
    margin: 0 auto;
}

/* ====== BREADCRUMB ====== */
.profile-breadcrumb {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--pp-text-muted);
    margin-bottom: 16px;
    padding: 0 4px;
}
.profile-breadcrumb a {
    color: var(--pp-accent);
    text-decoration: none;
    transition: color 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
}
.profile-breadcrumb a:hover {
    color: #1a4fb3;
}
.profile-breadcrumb .sep {
    color: var(--pp-border);
}
.profile-breadcrumb .current {
    font-weight: 500;
    color: var(--pp-text);
}

/* ====== PROFILE HEADER ====== */
.profile-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px;
    background: white !important;
    padding: 24px 28px;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--pp-border);
    margin-bottom: 20px;
}
.profile-header-icon {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #fff;
    background: var(--pp-accent);
}
.profile-header-body {
    flex: 1;
    min-width: 180px;
}
.profile-header-body h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 6px 0;
}
.profile-header-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    font-size: 13px;
    color: var(--pp-text-muted);
}
.profile-header-meta span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.profile-header-meta i {
    width: 16px;
    font-size: 13px;
    color: var(--pp-text-muted);
}
.profile-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-left: auto;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    border: none;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
    background: var(--pp-bg);
    color: var(--pp-text);
    text-decoration: none;
}
.action-btn:hover {
    background: #e9edf4;
}
.action-btn-primary {
    background: var(--pp-accent);
    color: #fff;
}
.action-btn-primary:hover {
    background: #1f5cd4;
}
.action-btn-back {
    background: transparent;
    color: var(--pp-text-muted);
}
.action-btn-back:hover {
    background: var(--pp-bg);
}
.action-btn-danger {
    color: #b91c1c;
}
.action-btn-danger:hover {
    background: #fee2e2;
}

/* ====== BADGE ====== */
.profile-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}
.profile-badge .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
}
.c-success {
    background: #dcfce7;
    color: #166534;
}
.c-success .dot { background: #22c55e; }

.c-warning {
    background: #fef3c7;
    color: #92400e;
}
.c-warning .dot { background: #f59e0b; }

.c-danger {
    background: #fee2e2;
    color: #991b1b;
}
.c-danger .dot { background: #ef4444; }

.c-info {
    background: #e0f2fe;
    color: #075985;
}
.c-info .dot { background: #0ea5e9; }

.c-accent {
    background: var(--pp-accent-light);
    color: var(--pp-accent);
}
.c-secondary {
    background: #f1f5f9;
    color: #475569;
}

/* ====== STATS ====== */
.profile-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
}
.profile-stat-card {
    background: white !important;
    border: 1px solid var(--pp-border);
    border-radius: 12px;
    padding: 16px 18px;
    display: flex;
    align-items: center;
    gap: 14px;
}
.profile-stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.profile-stat-label {
    font-size: 12px;
    color: var(--pp-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.profile-stat-value {
    font-size: 17px;
    font-weight: 600;
}

/* ====== CARDS ====== */
.profile-card {
    background: white !important;
    border: 1px solid var(--pp-border);
    border-radius: 12px;
    height:auto !important;
}
.profile-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px;
    background: var(--pp-bg);
    border-bottom: 1px solid var(--pp-border);
}
.profile-section-title {
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}
.profile-section-title i {
    width: 18px;
    font-size: 14px;
}
.profile-count-pill {
    background: var(--pp-bg);
    padding: 2px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: var(--pp-text-muted);
}
.profile-card-body {
    padding: 18px;
    background: var(--pp-card-bg);
}

/* ====== FIELDS ====== */
.profile-field-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px 20px;
}
.profile-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.profile-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--pp-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.profile-value {
    font-size: 14px;
    font-weight: 500;
}

/* ====== TABLE ====== */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
}
.table-responsive::-webkit-scrollbar {
    width: 8px;
}
.table-responsive::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
}
.table-responsive::-webkit-scrollbar-track {
    background: transparent;
}
.profile-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}
.profile-table thead {
    background: #fafcff;
}
.profile-table th {
    text-align: left;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    color: var(--pp-text-muted);
    letter-spacing: 0.3px;
    border-bottom: 1px solid var(--pp-border);
}
.profile-table td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--pp-border);
    vertical-align: middle;
}
.profile-table tbody tr:hover {
    background: #fafcff;
}
.profile-table .profile-badge {
    font-size: 11px;
    padding: 2px 10px;
}

/* ====== ATTACHMENTS ====== */
.profile-attachment {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--pp-bg);
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid var(--pp-border);
}
.profile-attachment-thumb {
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--pp-border);
}
.profile-attachment-meta {
    display: flex;
    flex-direction: column;
}
.profile-attachment-name {
    font-weight: 500;
    font-size: 13px;
}
.profile-attachment-size {
    font-size: 11px;
    color: var(--pp-text-muted);
}
.profile-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
}
.profile-avatar.c-warning {
    background: #fef3c7;
    color: #92400e;
}
.profile-avatar.c-accent {
    background: var(--pp-accent-light);
    color: var(--pp-accent);
}
.profile-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 12px;
    text-align: center;
    gap: 4px;
}
.profile-empty i {
    font-size: 28px;
    color: var(--pp-text-muted);
    opacity: 0.4;
}
.profile-empty p {
    margin: 4px 0 0;
    font-weight: 500;
    color: var(--pp-text-muted);
}

/* ====== RESPONSIVE ====== */
@media (max-width: 768px) {
    .profile-header {
        flex-direction: column;
        align-items: stretch;
        text-align: left;
        padding: 16px;
    }
    .profile-actions {
        margin-left: 0;
        justify-content: flex-start;
    }
    .profile-header-meta {
        gap: 10px;
        font-size: 12px;
    }
    .profile-stats {
        grid-template-columns: repeat(2, 1fr);
    }
    .profile-field-grid {
        grid-template-columns: 1fr;
    }
    .profile-table {
        font-size: 12px;
    }
    .profile-table th,
    .profile-table td {
        padding: 8px 10px;
    }
}
@media (max-width: 480px) {
    .profile-stats {
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }
    .profile-stat-card {
        padding: 12px;
    }
}
</style>